import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { alerts as alertsApi } from "@/lib/api";
import type { Alert } from "@/types";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { PageHeader } from "@/components/layout/PageHeader";
import { Icon } from "@/components/ui/icon";
import { AlertSummary } from "@/components/alerts/AlertSummary";
import { timeAgo, pageTypeLabel } from "@/lib/utils";
import { cn } from "@/lib/utils";
import { useWorkspaceRefresh } from "@/hooks/useWorkspaceRefresh";

const PAGE_SIZE = 25;

const SIGNAL_NODE: Record<string, string> = {
  high: "bg-red-500 border-red-500",
  medium: "bg-amber-500 border-amber-500",
  low: "bg-zinc-400 border-zinc-400",
};
const SIGNAL_LINE: Record<string, string> = {
  high: "bg-red-500/20",
  medium: "bg-amber-500/20",
  low: "bg-zinc-400/20",
};

const FILTERS = [
  { label: "All", value: "all" },
  { label: "High", value: "high" },
  { label: "Medium", value: "medium" },
  { label: "Low", value: "low" },
];
const STATUS_FILTERS = [
  { label: "Unread", value: "unread" },
  { label: "Read", value: "read" },
  { label: "All", value: "all" },
];

export function AlertsPage() {
  const [alertList, setAlertList] = useState<Alert[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [signalFilter, setSignalFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const load = useCallback(async () => {
    setIsLoading(true);
    const res = await alertsApi.list({
      page, limit: PAGE_SIZE,
      signalScore: signalFilter !== "all" ? signalFilter : undefined,
      status: statusFilter !== "all" ? statusFilter : undefined,
    });
    if ("data" in res && res.data) {
      setAlertList(res.data.alerts);
      setTotal(res.data.total);
    }
    setIsLoading(false);
  }, [page, signalFilter, statusFilter]);

  // Refresh data when workspace changes
  useWorkspaceRefresh(load);

  // Also handle manual filter/pagination changes
  useEffect(() => { load(); }, [load]);

  const markAllRead = async () => {
    const res = await alertsApi.markAllRead();
    if ("data" in res && res.data) {
      toast.success(`Marked ${res.data.updated} alerts as read`);
      load();
    }
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const unreadCount = alertList.filter(a => a.status === "unread").length;

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title="Alerts"
        actions={
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <span className="font-mono text-[11px] text-muted-foreground">{unreadCount} unread</span>
            )}
            <Button variant="ghost" size="sm" onClick={markAllRead} className="text-[11px]">
              <Icon name="tick-double-01" size={11} className="mr-1.5" />
              Mark all read
            </Button>
          </div>
        }
      />

      <div className="flex-1 overflow-y-auto">
        {/* Filter bar */}
        <div className="flex items-center gap-1 px-6 py-3 border-b border-border">
          <div className="flex items-center gap-1 mr-4">
            {FILTERS.map(f => (
              <button
                key={f.value}
                onClick={() => { setSignalFilter(f.value); setPage(1); }}
                className={cn(
                  "font-mono text-[11px] uppercase tracking-widest px-2.5 py-1 rounded transition-colors",
                  signalFilter === f.value
                    ? "bg-foreground text-background"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="h-3 w-px bg-border mx-1" />
          <div className="flex items-center gap-1">
            {STATUS_FILTERS.map(f => (
              <button
                key={f.value}
                onClick={() => { setStatusFilter(f.value); setPage(1); }}
                className={cn(
                  "font-mono text-[11px] uppercase tracking-widest px-2.5 py-1 rounded transition-colors",
                  statusFilter === f.value
                    ? "bg-foreground text-background"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                {f.label}
              </button>
            ))}
          </div>
          <span className="ml-auto font-mono text-[11px] text-muted-foreground">{total} total</span>
        </div>

        {/* Pipeline list */}
        <div className="px-6 py-4">
          {isLoading ? (
            <div className="space-y-0">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="flex gap-4 py-3">
                  <div className="flex flex-col items-center shrink-0 w-5">
                    <Skeleton className="h-3 w-3 rounded-sm" />
                    {i < 5 && <div className="w-px flex-1 bg-border mt-1" />}
                  </div>
                  <div className="flex-1 space-y-2 pb-4">
                    <Skeleton className="h-3 w-40" />
                    <Skeleton className="h-3 w-full" />
                    <Skeleton className="h-2.5 w-32" />
                  </div>
                </div>
              ))}
            </div>
          ) : alertList.length === 0 ? (
            <div className="flex flex-col items-center gap-3 py-20 text-center">
              <Icon name="notification-01" size={28} className="text-muted-foreground/20" />
              <p className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">No alerts found</p>
              <p className="font-sans text-xs text-muted-foreground max-w-xs">
                Alerts appear here when changes are detected on your watched pages.
              </p>
            </div>
          ) : (
            <div>
              {alertList.map((alert, i) => {
                const isLast = i === alertList.length - 1;
                return (
                  <Link
                    key={alert.id}
                    to={`/alerts/${alert.id}`}
                    className="flex gap-4 group hover:bg-accent/20 transition-colors rounded -mx-2 px-2"
                  >
                    {/* Pipeline track */}
                    <div className="flex flex-col items-center shrink-0 w-5 pt-3.5">
                      <div className={cn(
                        "h-3 w-3 rounded-sm border-2 shrink-0 transition-all group-hover:scale-110",
                        SIGNAL_NODE[alert.signalScore] ?? "bg-zinc-400 border-zinc-400"
                      )} />
                      {!isLast && (
                        <div className={cn("w-px flex-1 mt-1 min-h-[20px]", SIGNAL_LINE[alert.signalScore] ?? "bg-border/50")} />
                      )}
                    </div>

                    {/* Content */}
                    <div className={cn("flex-1 min-w-0 py-3", !isLast && "pb-4")}>
                      <div className="flex items-center gap-2 mb-1">
                        {alert.competitor && (
                          <div className="flex items-center gap-1.5">
                            <img
                              src={`https://www.google.com/s2/favicons?domain=${alert.competitor.domain}&sz=16`}
                              alt="" className="h-3 w-3 rounded shrink-0"
                              onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                            />
                            <span className="font-mono text-[11px] uppercase tracking-widest text-foreground font-semibold">
                              {alert.competitor.name}
                            </span>
                          </div>
                        )}
                        <span className="font-mono text-[11px] text-muted-foreground">·</span>
                        <span className="font-mono text-[11px] text-muted-foreground">{pageTypeLabel(alert.pageType)}</span>
                        {alert.status === "unread" && (
                          <span className="h-1.5 w-1.5 rounded-full bg-foreground shrink-0" />
                        )}
                        <span className="ml-auto font-mono text-[11px] text-muted-foreground shrink-0">
                          {timeAgo(alert.detectedAt)}
                        </span>
                      </div>
                      <AlertSummary summary={alert.summary} className="font-mono text-[11px] text-foreground/60 line-clamp-2 leading-relaxed" />
                      {alert.watchedUrl && (
                        <p className="mt-1 font-mono text-[11px] text-muted-foreground/50 truncate">
                          {alert.watchedUrl.url}
                        </p>
                      )}
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-3 border-t border-border">
            <span className="font-mono text-[11px] text-muted-foreground">
              Page {page} of {totalPages}
            </span>
            <div className="flex gap-1">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="font-mono text-[11px] px-3 py-1.5 border border-border rounded hover:bg-accent transition-colors disabled:opacity-40 text-muted-foreground"
              >
                <span className="inline-flex items-center gap-1"><Icon name="arrow-left-01" size={11} />Prev</span>
              </button>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="font-mono text-[11px] px-3 py-1.5 border border-border rounded hover:bg-accent transition-colors disabled:opacity-40 text-muted-foreground"
              >
                <span className="inline-flex items-center gap-1">Next<Icon name="arrow-right-01" size={11} /></span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
